import { useEffect, useState } from 'react';
import {
    fetchLandingCurrencyFromGeolocation,
    LANDING_CURRENCY_FALLBACK,
    type LandingCurrency,
} from '../lib/landingCurrency';

type UseLandingCurrencyResult = {
    currency: LandingCurrency;
    isLoading: boolean;
};

/**
 * Resolves landing-page pricing currency from geo when no override is provided.
 * Falls back to EUR while loading or when geolocation is unavailable.
 */
export function useLandingCurrency(override?: LandingCurrency): UseLandingCurrencyResult {
    const [currency, setCurrency] = useState<LandingCurrency>(override ?? LANDING_CURRENCY_FALLBACK);
    const [isLoading, setIsLoading] = useState(override === undefined);

    useEffect(() => {
        if (override !== undefined) {
            setCurrency(override);
            setIsLoading(false);
            return undefined;
        }

        let cancelled = false;

        const resolveCurrency = async (): Promise<void> => {
            try {
                const detectedCurrency = await fetchLandingCurrencyFromGeolocation();
                if (!cancelled) {
                    setCurrency(detectedCurrency);
                }
            } catch {
                if (!cancelled) {
                    setCurrency(LANDING_CURRENCY_FALLBACK);
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };

        void resolveCurrency();

        return () => {
            cancelled = true;
        };
    }, [override]);

    return { currency, isLoading };
}
